"use client"

import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { 
  Home, 
  CalendarPlus, 
  User, 
  MapPin, 
  Heart, 
  Baby, 
  Moon, 
  Leaf,
  Phone,
  Mail,
  ArrowRight
} from 'lucide-react'
import { trackEvent } from '@/lib/analytics'

const quickLinks = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Book an Appointment', path: '/appointments', icon: CalendarPlus },
  { name: 'Dr Renee Knott', path: '/dr-renee-knott', icon: User },
  { name: 'Clinic Locations', path: '/clinics', icon: MapPin },
]

const serviceLinks = [
  { name: 'Fertility & Preconception', path: '/what-we-help-with/fertility-preconception', icon: Baby },
  { name: 'Pregnancy & Postpartum', path: '/what-we-help-with/pregnancy-postpartum', icon: Heart },
  { name: 'Menstrual & Hormonal Health', path: '/what-we-help-with/menstrual-hormonal-health', icon: Moon },
  { name: 'Stress, Sleep & Digestion', path: '/what-we-help-with/stress-sleep-digestion', icon: Leaf },
]

export default function NotFoundPage() {
  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Page Not Found | Raintree Chinese Medicine</title>
        <meta name="description" content="The page you're looking for doesn't exist. Find your way to our acupuncture and Chinese medicine services." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl lg:text-5xl text-white mb-4">
              Page Not Found
            </h1>
            <p className="text-white/90 text-lg lg:text-xl mb-8">
              Sorry, we couldn't find the page you're looking for. It may have been moved or no longer exists.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                asChild
                className="bg-white text-gray-800 hover:bg-gray-100"
              >
                <Link to="/">
                  <Home className="h-4 w-4 mr-2" />
                  Go to Homepage
                </Link>
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => openBooking('404_page')}
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Book an Appointment
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl text-[#3f4141] text-center mb-8">
              Quick Links
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#a48b61] hover:bg-gray-50 transition-all group"
                  >
                    <link.icon className="h-5 w-5 text-[#a48b61]" />
                    <span className="text-gray-700 group-hover:text-[#a48b61] transition-colors">
                      {link.name}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Help With Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl text-[#3f4141] text-center mb-4">
              What We Help With
            </h2>
            <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
              Explore our key areas of care in acupuncture and Chinese medicine.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {serviceLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className="flex items-center justify-between p-4 rounded-lg bg-white border border-gray-200 hover:border-[#a48b61] hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <link.icon className="h-5 w-5 text-[#a48b61]" />
                      <span className="text-gray-700 group-hover:text-[#a48b61] transition-colors">
                        {link.name}
                      </span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-[#a48b61] transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link
                to="/what-we-help-with"
                className="text-[#a48b61] hover:text-[#8a7555] font-medium inline-flex items-center gap-1 transition-colors"
              >
                View all services
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-2xl md:text-3xl text-[#3f4141] mb-4">
              Need Help?
            </h2>
            <p className="text-gray-600 mb-8">
              If you can't find what you're looking for, please get in touch and we'll be happy to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+61752409192"
                className="flex items-center gap-2 text-gray-600 hover:text-[#a48b61] transition-colors"
                onClick={() => trackEvent('call_click', { location: '404_page' })}
              >
                <Phone className="h-5 w-5" />
                <span>+61 7 5240 9192</span>
              </a>
              <span className="hidden sm:inline text-gray-300">|</span>
              <a
                href="mailto:admin@raintreechinesemedicine.com.au"
                className="flex items-center gap-2 text-gray-600 hover:text-[#a48b61] transition-colors"
              >
                <Mail className="h-5 w-5" />
                <span>admin@raintreechinesemedicine.com.au</span>
              </a>
            </div>
            <div className="mt-8">
              <Button
                asChild
                className="bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
              >
                <Link to="/contact">
                  Contact Us
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}
